import {
    Area,
    AreaChart,
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import {
    chartGridStroke,
    chartTooltipStyle,
    rtlXAxisProps,
    rtlYAxisProps,
    tickFormatter,
} from '@/features/reports/lib/chartTheme';
import { formatNumber, formatToman, categoryAxisTick } from '@/features/reports/lib/format';

const METRIC_META = {
    revenue: { label: 'درآمد', money: true, color: '#2E7D32' },
    orders: { label: 'تعداد سفارش', money: false, color: '#1E88E5' },
    units: { label: 'تعداد کالا', money: false, color: '#8E24AA' },
    aov: { label: 'میانگین سفارش', money: true, color: '#F59E0B' },
};

/** Persian tooltip that formats money and counts correctly per metric. */
const TrendTooltip = ({ active, payload, metric }) => {
    if (!active || !payload?.length) return null;
    const point = payload[0].payload;
    const meta = METRIC_META[metric] ?? METRIC_META.revenue;

    return (
        <div className="report-tooltip">
            <p className="report-tooltip-title">{point.label}</p>
            <p className="report-tooltip-row">
                <span>{meta.label}</span>
                <strong>{meta.money ? formatToman(point[metric]) : formatNumber(point[metric])}</strong>
            </p>
            <p className="report-tooltip-row report-tooltip-muted">
                <span>سفارش</span>
                <span>{formatNumber(point.orders)}</span>
            </p>
            <p className="report-tooltip-row report-tooltip-muted">
                <span>کالا</span>
                <span>{formatNumber(point.units)}</span>
            </p>
        </div>
    );
};

/**
 * Revenue / orders / units over the selected granularity.
 *
 * Renders as an area chart for money metrics and a line chart for counts —
 * filled areas read well for magnitudes, lines for discrete counts.
 */
export default function TrendChart({ points = [], metric = 'revenue', height = 320 }) {
    const meta = METRIC_META[metric] ?? METRIC_META.revenue;
    const isMoney = meta.money;

    const common = (
        <>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chartGridStroke} />
            <XAxis dataKey="label" {...rtlXAxisProps} tickFormatter={categoryAxisTick} minTickGap={16} />
            <YAxis {...rtlYAxisProps} tickFormatter={tickFormatter} />
            <Tooltip
                content={<TrendTooltip metric={metric} />}
                contentStyle={chartTooltipStyle}
                cursor={{ stroke: chartGridStroke }}
            />
        </>
    );

    return (
        <div style={{ width: '100%', height }}>
            <ResponsiveContainer width="100%" height="100%">
                {isMoney ? (
                    <AreaChart data={points} margin={{ top: 10, right: 10, left: 10, bottom: 5 }}>
                        <defs>
                            <linearGradient id={`trend-${metric}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={meta.color} stopOpacity={0.35} />
                                <stop offset="100%" stopColor={meta.color} stopOpacity={0.02} />
                            </linearGradient>
                        </defs>
                        {common}
                        <Area
                            type="monotone"
                            dataKey={metric}
                            stroke={meta.color}
                            strokeWidth={2}
                            fill={`url(#trend-${metric})`}
                            name={meta.label}
                        />
                    </AreaChart>
                ) : (
                    <LineChart data={points} margin={{ top: 10, right: 10, left: 10, bottom: 5 }}>
                        {common}
                        <Line
                            type="monotone"
                            dataKey={metric}
                            stroke={meta.color}
                            strokeWidth={2}
                            dot={{ r: 2 }}
                            activeDot={{ r: 4 }}
                            name={meta.label}
                        />
                    </LineChart>
                )}
            </ResponsiveContainer>
        </div>
    );
}

export { METRIC_META };
